/**
 * Assign a voucher code to a customer -- phase 6b, Task 3.
 * PUT /admin/discounts/vouchers/:id/assignment `{ userId, redeemableByAssigneeOnly }`.
 *
 * `CustomerPicker` (also used by CreateCodeDialog) reuses the SAME request
 * as the Customers area's lookup (`customers/CustomerLookup.tsx`):
 * `GET admin/users/customers/search?search=<term>` over the shared
 * `authInstance`. That component navigates on selection so it is not
 * reusable here; the request shape is. A pasted user id (uuid) is accepted
 * directly and skips the search. That endpoint runs `assertAdminAccess`
 * (owner/admin) on the backend, so the picker only offers SEARCH to
 * `useCanWriteDiscounts()` roles and is paste-an-id only for the rest (fix
 * wave I1) -- the dialogs that mount it are write-only anyway.
 *
 * The API refuses to reassign a REFERRAL code (422) -- that `userMessage`
 * is rendered verbatim inside the dialog. Unassigning is NOT done here (it
 * needs no picker); CodesTab does it, with a confirm on reward codes.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { authInstance } from '../../../services/httpClient';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { CUSTOMER_SEARCH_RESTRICTED_HINT, useCanWriteDiscounts } from '../roles';
import type { VoucherRow } from '../types';

export type PickedCustomer = { id: string; label: string };

type SearchCustomer = { id: string; email: string; firstName: string; lastName: string | null; phoneNumber: string | null };

/** uuid v1-v5, case-insensitive -- the shape of every user id in the app. */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const customerLabel = (c: Pick<SearchCustomer, 'firstName' | 'lastName' | 'email'>): string =>
  `${`${c.firstName} ${c.lastName ?? ''}`.trim()} · ${c.email}`;

export interface CustomerPickerProps {
  value: PickedCustomer | null;
  onChange: (next: PickedCustomer | null) => void;
  /** 422 `assignedUserId`/`userId` message from the API, shown under the field. */
  error?: string | null;
  disabled?: boolean;
  label?: string;
}

/** Search-or-paste a customer; resolves to `{ id, label }`. */
export const CustomerPicker: React.FC<CustomerPickerProps> = ({ value, onChange, error, disabled, label = 'Customer' }) => {
  const canSearch = useCanWriteDiscounts();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchCustomer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const isId = UUID_RE.test(term.trim());

  const run = async () => {
    const v = term.trim();
    if (!v) return;
    if (UUID_RE.test(v)) {
      onChange({ id: v, label: v });
      setResults(null);
      return;
    }
    if (!canSearch) {
      setSearchError(`That is not a customer id. ${CUSTOMER_SEARCH_RESTRICTED_HINT}`);
      return;
    }
    setLoading(true);
    setSearchError(null);
    try {
      const res = await authInstance.get('admin/users/customers/search', { params: { search: v } });
      setResults((res.data?.customers ?? []) as SearchCustomer[]);
    } catch (err) {
      setSearchError(toApiFailure(err).message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  if (value) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label={label}
          value={value.label}
          size="small"
          fullWidth
          error={Boolean(error)}
          helperText={error ?? undefined}
          slotProps={{ input: { readOnly: true } }}
        />
        <Button size="small" onClick={() => onChange(null)} disabled={disabled}>
          Change
        </Button>
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          label={label}
          placeholder={canSearch ? 'Name, email, phone — or paste a customer id' : 'Paste a customer id'}
          size="small"
          fullWidth
          value={term}
          disabled={disabled}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              run();
            }
          }}
          error={Boolean(error)}
          helperText={error ?? (canSearch ? 'Search by name, email or phone, or paste a customer id.' : CUSTOMER_SEARCH_RESTRICTED_HINT)}
          slotProps={{
            input: {
              startAdornment: canSearch ? (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
        <Button
          variant="outlined"
          onClick={run}
          disabled={disabled || loading || !term.trim()}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
        >
          {isId || !canSearch ? 'Use id' : 'Search'}
        </Button>
      </Stack>
      {searchError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {searchError}
        </Alert>
      )}
      {results && results.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No customers found.
        </Typography>
      )}
      {results && results.length > 0 && (
        <List dense sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 1, maxHeight: 220, overflowY: 'auto' }}>
          {results.map((c) => (
            <ListItemButton key={c.id} divider onClick={() => onChange({ id: c.id, label: customerLabel(c) })}>
              <ListItemText
                primary={`${c.firstName} ${c.lastName ?? ''}`.trim()}
                secondary={[c.email, c.phoneNumber].filter(Boolean).join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export interface AssignCodeDialogProps {
  open: boolean;
  onClose: () => void;
  voucher: VoucherRow | null;
}

export const AssignCodeDialog: React.FC<AssignCodeDialogProps> = ({ open, onClose, voucher }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<PickedCustomer | null>(null);
  const [assigneeOnly, setAssigneeOnly] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPicked(null);
      setAssigneeOnly(true);
      setError(null);
      setFieldError(null);
    }
  }, [open]);

  const assign = useMutation({
    mutationFn: (args: { id: string; userId: string; redeemableByAssigneeOnly: boolean }) =>
      discountsApi.assignVoucher(args.id, { userId: args.userId, redeemableByAssigneeOnly: args.redeemableByAssigneeOnly }),
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: qk.all });
      notify(`${saved.code} assigned`, { type: 'success' });
      onClose();
    },
    onError: (err) => {
      const f = toApiFailure(err);
      const forUser = f.fieldErrors.userId ?? f.fieldErrors.assignedUserId ?? null;
      setFieldError(forUser);
      // A REFERRAL code refuses reassignment (422, path '' or the whole body) -- show the API copy verbatim.
      setError(forUser ? null : f.message);
    },
  });

  const busy = assign.isPending;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign {voucher?.code ?? 'code'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {voucher?.assignedUser
            ? `Currently assigned to ${voucher.assignedUser.email}. Picking another customer moves the code to them.`
            : 'Pick the customer this code belongs to.'}
        </DialogContentText>
        {voucher?.kind === 'REFERRAL' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Referral codes belong to the customer who earned them; the engine refuses to reassign them.
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <CustomerPicker value={picked} onChange={setPicked} error={fieldError} disabled={busy} />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={assigneeOnly} onChange={(e) => setAssigneeOnly(e.target.checked)} disabled={busy} />}
          label="Only the assignee can redeem this code"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !picked || !voucher}
          onClick={() => voucher && picked && assign.mutate({ id: voucher.id, userId: picked.id, redeemableByAssigneeOnly: assigneeOnly })}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignCodeDialog;
